import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  ChevronDown,
  Globe,
  Truck,
  FileSearch,
  Package,
  Users,
  Network,
  Info,
  Shield,
  Phone,
  MessageSquare,
  LayoutGrid,
  Menu,
  X,
  User,
  UserPlus,
  Settings,
  LogOut,
  Coins,
  Sun,
  Moon
} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'

const MOLOCHAIN_URLS = {
  main: 'https://molochain.com',
  admin: 'https://admin.molochain.com',
  auth: 'https://auth.molochain.com',
  opt: 'https://opt.molochain.com',
  mololink: 'https://mololink.molochain.com',
  molo: 'https://molo.molochain.com',
  db: 'https://db.molochain.com'
}

interface NavDropdownItem {
  href: string
  label: string
  description: string
  icon: React.ReactNode
}

interface NavItem {
  label: string
  href?: string
  items?: NavDropdownItem[]
}

const navItems: NavItem[] = [
  {
    label: 'Home',
    href: MOLOCHAIN_URLS.main
  },
  {
    label: 'Services',
    items: [
      { href: `${MOLOCHAIN_URLS.main}/services`, label: 'All Services', description: 'View our complete service catalog', icon: <Package className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/tracking`, label: 'Track Shipment', description: 'Real-time shipment tracking', icon: <Truck className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/contact`, label: 'Get Quote', description: 'Request a custom quote', icon: <MessageSquare className="h-4 w-4" /> }
    ]
  },
  {
    label: 'Ecosystem',
    items: [
      { href: MOLOCHAIN_URLS.mololink, label: 'MOLOLINK', description: 'Professional logistics network', icon: <Network className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/departments`, label: 'Departments', description: 'Access department dashboards', icon: <Globe className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/partners`, label: 'Partners', description: 'Our trusted partners', icon: <Users className="h-4 w-4" /> }
    ]
  },
  {
    label: 'Resources',
    items: [
      { href: `${MOLOCHAIN_URLS.main}/terms`, label: 'Terms of Service', description: 'Legal terms and policies', icon: <FileSearch className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/privacy`, label: 'Privacy Policy', description: 'Data protection and privacy', icon: <Shield className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/contact`, label: 'Contact Us', description: 'Get in touch with our team', icon: <Phone className="h-4 w-4" /> }
    ]
  },
  {
    label: 'Company',
    items: [
      { href: `${MOLOCHAIN_URLS.main}/about`, label: 'About Us', description: 'Learn about MoloChain', icon: <Info className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/partners`, label: 'Partners', description: 'Our trusted partners', icon: <Users className="h-4 w-4" /> },
      { href: `${MOLOCHAIN_URLS.main}/contact`, label: 'Contact', description: 'Get in touch with us', icon: <Phone className="h-4 w-4" /> }
    ]
  }
]

function DropdownMenu({ items, isOpen, onClose }: { items: NavDropdownItem[], isOpen: boolean, onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div ref={ref} className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 rounded-xl border border-border bg-popover shadow-lg py-2 z-[100]">
      {items.map((item) => (
        <a
          key={item.label}
          href={item.href}
          target={item.href.startsWith('http') ? '_blank' : undefined}
          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
          className="flex items-start gap-3 px-4 py-2.5 hover:bg-accent transition-colors"
          onClick={onClose}
        >
          <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary flex-shrink-0">
            {item.icon}
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">{item.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{item.description}</div>
          </div>
        </a>
      ))}
    </div>
  )
}

function AuthButton() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
        aria-label="Authentication options"
      >
        <UserPlus className="h-5 w-5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-popover shadow-lg py-1 z-[100]">
          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Authentication
          </div>
          <div className="h-px bg-border mx-2" />
          <a
            href={`${MOLOCHAIN_URLS.auth}/login?redirect=${encodeURIComponent(window.location.href)}`}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
            onClick={() => setOpen(false)}
          >
            <User className="h-4 w-4 text-muted-foreground" />
            Login
          </a>
          <a
            href={`${MOLOCHAIN_URLS.auth}/register?redirect=${encodeURIComponent(window.location.href)}`}
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
            onClick={() => setOpen(false)}
          >
            <UserPlus className="h-4 w-4 text-muted-foreground" />
            Register
          </a>
        </div>
      )}
    </div>
  )
}

function ProfileMenu({ user, onLogout }: { user: any, onLogout: () => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const initials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.username?.charAt(0)?.toUpperCase() || 'U'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-xs font-semibold hover:opacity-90 transition-all"
        aria-label="User menu"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-popover shadow-lg py-1 z-[100]">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-foreground">{user?.name || user?.username}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
          </div>
          <div className="h-px bg-border mx-2" />
          <a
            href="/dashboard"
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
            onClick={() => setOpen(false)}
          >
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            Dashboard
          </a>
          <a
            href="https://molochain.com/profile"
            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground hover:bg-accent transition-colors"
            onClick={() => setOpen(false)}
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            My Profile
          </a>
          <div className="h-px bg-border mx-2" />
          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-destructive hover:bg-accent transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  )
}

function ThemeToggleButton() {
  const { effectiveTheme, setTheme } = useTheme()
  return (
    <button
      onClick={() => setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')}
      className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
      title="Toggle theme"
    >
      {effectiveTheme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
    </button>
  )
}

export function EcosystemNavbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout } = useAuth()

  const handleToggle = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label)
  }

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-background">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href={MOLOCHAIN_URLS.main} className="flex items-center gap-2 flex-shrink-0" target="_blank" rel="noopener noreferrer">
          <img src="/molochain-logo.jpeg" alt="MOLOCHAIN" className="h-10 w-auto" />
        </a>

        <div className="hidden md:flex items-center gap-2">
          {navItems.map((item) => (
            <div key={item.label} className="relative">
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent transition-colors"
                >
                  {item.label}
                </a>
              ) : (
                <>
                  <button
                    onClick={() => handleToggle(item.label)}
                    className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                      openDropdown === item.label
                        ? 'text-primary bg-accent'
                        : 'text-foreground/80 hover:text-primary hover:bg-accent'
                    }`}
                  >
                    {item.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {item.items && (
                    <DropdownMenu
                      items={item.items}
                      isOpen={openDropdown === item.label}
                      onClose={() => setOpenDropdown(null)}
                    />
                  )}
                </>
              )}
            </div>
          ))}

          <a
            href={MOLOCHAIN_URLS.molo}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent transition-colors"
          >
            <Coins className="h-4 w-4" />
            Token
          </a>
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggleButton />

          <button
            className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
            title="Language"
          >
            <Globe className="h-[18px] w-[18px]" />
          </button>

          <div className="hidden sm:flex items-center ml-1 pl-2 border-l border-border">
            {user ? (
              <ProfileMenu user={user} onLogout={handleLogout} />
            ) : (
              <AuthButton />
            )}
          </div>

          <button
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.href ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : (
                  <>
                    <button
                      onClick={() => handleToggle(item.label)}
                      className="flex w-full items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent"
                    >
                      {item.label}
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === item.label && item.items && (
                      <div className="ml-3 mt-1 space-y-0.5">
                        {item.items.map((sub) => (
                          <a
                            key={sub.label}
                            href={sub.href}
                            target={sub.href.startsWith('http') ? '_blank' : undefined}
                            rel={sub.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-primary hover:bg-accent"
                            onClick={() => setMobileOpen(false)}
                          >
                            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary/15 text-primary flex-shrink-0">
                              {sub.icon}
                            </div>
                            {sub.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

            <a
              href={MOLOCHAIN_URLS.molo}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent"
              onClick={() => setMobileOpen(false)}
            >
              <Coins className="h-4 w-4" />
              Token
            </a>
          </div>
          {!user && (
            <div className="px-4 py-3 border-t border-border">
              <a
                href={`${MOLOCHAIN_URLS.auth}/login?redirect=${encodeURIComponent(window.location.href)}`}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 transition-colors"
              >
                <User className="h-4 w-4" />
                Login / Register
              </a>
            </div>
          )}
        </div>
      )}
    </nav>
  )
}
